import type { IBrandQuoteThemeConfig } from "@/@sdk/brands/brand-model";
import { getLighterColor } from "@/utils/colors";
import {
  buildGoogleFontsUrl,
  deriveSecondaryColor,
  formatFontFamily,
  getContrastColor,
  getDualToneBlockHeadingColor,
  getQuoteThemeStyleTokens,
} from "./quote-theme-class-maps";
import { getEffectiveQuoteThemeConfig } from "./quote-theme-presets";
import type {
  ResolveQuoteThemeInput,
  ResolvedQuoteTheme,
} from "./quote-theme-types";

function normalizeFontGroup(
  config: IBrandQuoteThemeConfig
): IBrandQuoteThemeConfig["fontGroup"] {
  if (config.fontSchema === "SINGLE_FONT") {
    return {
      ...config.fontGroup,
      bodyFont: config.fontGroup.headingFont,
    };
  }

  return config.fontGroup;
}

export function resolveQuoteTheme(
  input: ResolveQuoteThemeInput
): ResolvedQuoteTheme {
  const baseConfig = getEffectiveQuoteThemeConfig(
    input.config,
    input.brandPrimaryColor
  );

  const config: IBrandQuoteThemeConfig = {
    ...baseConfig,
    fontGroup: normalizeFontGroup(baseConfig),
  };

  const primary =
    input.overridePrimaryColor ||
    config.colorPalette.primary ||
    input.brandPrimaryColor ||
    "#F15933";

  const secondary = deriveSecondaryColor(
    primary,
    config.colorPalette.secondary,
    config.colorSchema
  );

  const styleTokens = getQuoteThemeStyleTokens(config.uiStyle);
  const headingFont = formatFontFamily(config.fontGroup.headingFont);
  const bodyFont = formatFontFamily(config.fontGroup.bodyFont);

  return {
    config,
    tokens: {
      primary,
      secondary,
      primary10: getLighterColor(primary, 0.1),
      primary20: getLighterColor(primary, 0.2),
      onPrimary: getContrastColor(primary),
      blockHeadingColor:
        config.colorSchema === "DUAL_TONE"
          ? getDualToneBlockHeadingColor(primary, secondary)
          : primary,
      headingFont,
      bodyFont,
      radiusCard: styleTokens.radiusCard,
      radiusPill: styleTokens.radiusPill,
      shadowCard: styleTokens.shadowCard,
      sectionGap: styleTokens.sectionGap,
    },
    googleFontsUrl: buildGoogleFontsUrl(
      config.fontGroup.headingFont,
      config.fontGroup.bodyFont
    ),
    uiStyle: config.uiStyle,
    layoutConfig: config.layoutConfig,
    navBarTemplate:
      input.quoteOverrides?.navBarTemplate ??
      config.navBarTemplate ??
      "TYPE_2",
    primaryColorFillMode:
      input.quoteOverrides?.primaryColorFillMode ??
      config.primaryColorFillMode ??
      "Fill",
    headerTemplate:
      input.quoteOverrides?.headerTemplate ??
      config.headerTemplate ??
      "Default",
  };
}
